<template>
  <div class="fin-watchlist-details">
    <Portal to="breadcrumb_watchlistDetails">
      {{ !$route.meta.newPage ? watchlistTitle : watchlistTitle.toUpperCase() }}
    </Portal>


    <fin-data-loader v-if="!$route.meta.newPage" :resolver="fetchData">

      <fin-choose-selector v-model="addToWatchlistDialog"
                           :options="assetDistribution"
                           :preSelectedItem="assetDistribution[0]"
                           :title="`Wertpapier zu ${watchlistTitle} hinzufügen`"
                           ok-button-label="Weiter"
                           select-box-item-title="name"
                           select-box-item-value="id"
                           select-box-title="Assetklasse"
                           @submitSelectedItem="submitAssetClass"
      />

      <div v-if="watchlist && !loadingWatchlist">
        <template
          v-if="$route.name !== 'WatchlistNotification' && $route.name !== 'addWatchlistsInstrumentsToPortfolio'">

          <v-container v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
                       v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
                       fluid>
            <v-row :class="$vuetify.breakpoint.xs ? 'header pt-6 pb-2' : 'header pt-12 pb-5'">
              <v-col cols="9">
                <fin-editable-property ref="rename"
                                       :editFunction="(val) => updateWatchlistName(watchlist, val)"
                                       :orginal-value="watchlist.name"
                                       :value="watchlist.name"
                                       big-value-size
                                       class="name"
                                       submit-button="Umbenennen"
                                       title="Watchlist umbenennen">
                  <template #default="{model,change}" class="name">
                    <slot :item="watchlist" name="item-title"/>
                    <v-text-field :rules="[ v => !!v || 'Name wird benötigt']"
                                  :value="model"
                                  label="Watchlist Name"
                                  @input="change"
                    />
                  </template>

                  <template v-if="!watchlist.inactive" #subOriginalValue>
                    <fin-realtime-indicator :instruments="watchlistItems.map(item => item.instrument)" class="realtime"
                                            trading-platform="tradegate"/>
                  </template>
                </fin-editable-property>
              </v-col>

              <v-col class="d-flex justify-end" cols="3">
                <v-menu :nudge-bottom=10
                        :nudge-left=50
                        :offset-y="true"
                        bottom
                        content-class="menu">
                  <template #activator="{ on }">
                    <v-btn v-on="on" outlined icon tile>
                      <v-icon>icon-contextmenu</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item :disabled="watchlist.inactive" @click="showRenameWatchlistDialog">
                      <v-list-item-title>
                        Umbenennen
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="showDeleteWatchlistDialog">
                      <v-list-item-title>
                        Löschen
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
            </v-row>
          </v-container>

          <div class="content"
               v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
               v-style:paddingRight="$valueByBreakpoint($finPadding.right)">

            <fin-subscription-content v-if="watchlist.inactive"
                                      :feature-id="featureIds.WATCHLIST.id"
                                      :max-active-elements="maxWatchlists"
                                      :restriction-type="featureRestrictions.INACTIVE"
                                      class="mb-5"
                                      style="max-width: 700px;"/>

            <fin-data-table :headers="tableHeaders"
                            :resolver="watchlistItems"
                            class="pt-2"
                            fixed-header
                            sticky-first-col>

              <template #item.actions="{ item }">
                <div v-if="item.lowerLimit || item.upperLimit" style="display: inline-grid;">
                  <v-switch v-if="item.lowerLimit || item.upperLimit"
                            v-model="item.alertActive"
                            :color="$finColors.primaryDark"
                            :false-value="false"
                            :label="item.alertActive ? 'aktiv' : 'inaktiv'"
                            :true-value="true"
                            class="mt-0 pt-0 mr-2"
                            hide-details
                            inset
                            @change="changeAlertActive(item)"/>

                  <v-tooltip v-if="!item.alertActive && item.exceeded" bottom>
                    <template #activator="{ on, attrs }">
                       <span v-if="item.exceeded.timestamp"
                             v-bind="attrs"
                             v-on="on"
                             style="display: block; margin-top: 2px;">
                         <small>Deaktiviert am {{ item.exceeded.timestamp | date }}</small>
                       </span>
                    </template>
                    <span v-if="item.exceeded.lowerLimit">
                      Das Limit von {{ item.exceeded.lowerLimit | finCurrency }} wurde am {{
                        item.exceeded.timestamp | date
                      }}
                      mit dem Kurs {{ item.exceeded.price | finCurrency }} unterschritten
                    </span>
                    <span v-if="item.exceeded.upperLimit">
                      Das Limit von {{ item.exceeded.upperLimit | finCurrency }} wurde am {{
                        item.exceeded.timestamp | date
                      }}
                      mit dem Kurs {{ item.exceeded.price | finCurrency }} überschritten
                    </span>
                  </v-tooltip>
                </div>

                <v-btn v-if="item.instrument.close && !item.lowerLimit && !item.upperLimit"
                       class="mr-2"
                       outlined
                       small
                       :disabled="watchlist.inactive"
                       @click="gotoAlertsConfiguration(item)">
                  <v-icon :color="$finColors.primaryDark" class="mr-2">icon-edit</v-icon>
                  Limits
                </v-btn>

                <v-menu
                  left
                  offset-y
                >
                  <template #activator="{ on }">
                    <v-btn v-on="on"
                           :class="$vuetify.breakpoint.smAndDown ? 'mb-6' : 'px-2'"
                           icon
                           tile>
                      <v-icon class="fin-color-primary-dark--text">icon-contextmenu-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item
                      :disabled="watchlist.inactive || !item.instrument.close || !item.instrument.active || !item.instrument.covered || item.instrument.delisted"
                      @click="gotoAlertsConfiguration(item)">
                      <v-list-item-title
                        :style="{ color: !item.instrument.close  ? $finColors.accentGray2 : $finColors.primaryDark }">
                        Limits einstellen
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item :disabled="watchlist.inactive || !item.lowerLimit && !item.upperLimit"
                                 @click="deleteLimitsConfirm(item)">
                      <v-list-item-title
                        :style="{ color: (!item.lowerLimit && !item.upperLimit)  ? $finColors.accentGray2 : $finColors.primaryDark }">
                        Limits löschen
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="addItemsToPortfolio(item)"
                                 :disabled="watchlist.inactive || !item.instrument.active || !item.instrument.covered || item.instrument.delisted">
                      <v-list-item-title>
                        Zu Portfolio hinzufügen
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item :disabled="watchlist.inactive" @click="deleteWatchlistEntry(item)">
                      <v-list-item-title>
                        Löschen
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </fin-data-table>

            <v-btn :class="$vuetify.breakpoint.xs ? 'my-3' : 'my-6'" :disabled="watchlist.inactive" dark tile
                   @click="addToWatchlistDialog=true">
              Wertpapiere hinzufügen
            </v-btn>
          </div>
        </template>

        <router-view v-if="$route.name === 'addWatchlistsInstrumentsToPortfolio'"/>

        <router-view v-else/>
      </div>

      <div v-else-if="loadingWatchlist">
        <v-row class="pt-10 fin-color-primary-light"
               v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
               v-style:paddingRight="$valueByBreakpoint($finPadding.right)">
          <v-col cols="12" sm="7" lg="9">
            <fin-logo-full dark loading></fin-logo-full>
          </v-col>
        </v-row>
      </div>

    </fin-data-loader>

    <router-view v-else/>
  </div>
</template>

<script>
import {finUserAPI, finWatchlistAPI} from '@/js/finAPI'
import FinDataLoader from '@/components/common/DataLoader/index'
import FinEditableProperty from '@/components/pages/profile/EditableProperty'
import FinLogoFull from "@/components/common/logo/LogoFull"
import FinChooseSelector from "@/components/common/ChooseSelector"
import AssetClassFinder from "@/components/pages/onboarding/AssetClassFinder"
import AssetClasses from "@/mocks/AssetClasses"
import {TablePresets} from "@/components/common/DataTable/DefaultHeaders"
import FinDataTable from "@/components/common/DataTable/FinDataTable"
import {EventBus} from "@/plugins/fincraft"
import SubscriptionHandler from "@/mixins/SubscriptionHandler"
import {watchlistDetailsInstrumentSubscription, watchlistDetailsWLEntrySubscription} from "@/js/finSubscriptions"
import {mapGetters} from "vuex"
import FinRealtimeIndicator from "@/components/common/RealtimeIndicator"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import PriceModel from "@/mixins/PriceModel"
import FinSubscriptionContent from "@/components/common/subscription/content"

export default {
  name: 'FinWatchlistDetails',
  components: {
    FinSubscriptionContent,
    FinRealtimeIndicator,
    FinDataTable,
    FinChooseSelector,
    FinDataLoader,
    FinEditableProperty,
    FinLogoFull, /*...components*/
  },
  props: {
    /* Set via router */
    id: {
      type: String
    }
  },
  data() {
    return {
      watchlist: undefined,
      watchlistTitle: '',
      watchlistItems: undefined,
      addToWatchlistDialog: false,
      selected: [],
      loadingWatchlist: true,
      featureIds: PRICEMODEL_FEATURE_IDS,
      featureRestrictions: PRICEMODEL_FEATURE_RESTRICTIONS,
    }
  },
  mixins: [SubscriptionHandler, PriceModel],
  computed: {
    ...mapGetters('auth', ['userId', 'profile']),
    assetDistribution() {
      return AssetClassFinder.all
    },
    tableHeaders() {
      const actions = [
        {
          name: 'Limits einstellen',
          disabled: (item) => {
            return !item.instrument.close || !item.instrument.active || !item.instrument.covered || item.instrument.delisted
          },
          click: (item) => {
            this.gotoAlertsConfiguration(item)
          },
        },
        {
          name: 'Limits löschen',
          disabled: (item) => {
            return !item.lowerLimit && !item.upperLimit
          },
          click: (item) => {
            this.deleteLimitsConfirm(item)
          },
        },
        {
          name: 'Zu Portfolio hinzufügen',
          disabled: (item) => {
            return !item.instrument.active || !item.instrument.covered || item.instrument.delisted
          },
          click: (item) => {
            this.addItemsToPortfolio(item)
          }
        },
        {
          name: 'Löschen',
          click: (item) => {
            this.deleteWatchlistEntry(item)
          }
        },
      ]
      return TablePresets.createWatchlistHeaders(actions)
    },
    maxWatchlists() {
      return this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.WATCHLIST.id)
    },
  },
  methods: {
    handleInstrumentSubscription(newData, wlItem) {
      const {price, timestamp, priceChange, priceChangePercentage, high, low} = newData.data.onCreateRealTimePrice
      const instr = wlItem.instrument

      instr.latestPrice = newData.data.onCreateRealTimePrice
      instr.close = price
      instr.closeChange1D = priceChangePercentage
      instr.closeChangeAbsolute1D = priceChange
      instr.closeDate = timestamp
      instr.high = high
      instr.low = low

      this.watchlistItems = [...this.watchlistItems]
    },
    handleWLEntrySubscription(newData, wl) {
      const {alertActive, instrumentId, exceeded, lowerLimit, upperLimit} = newData.data.onUpdateWatchlistEntry
      const item = this.watchlistItems.find(item => item.instrument.id === instrumentId)

      item.alertActive = alertActive
      item.exceeded = exceeded
      item.lowerLimit = lowerLimit
      item.upperLimit = upperLimit

      this.watchlistItems = [...this.watchlistItems]
    },
    async fetchData() {
      this.watchlist = await finUserAPI.getWatchlist(this.id)
      this.watchlistTitle = this.watchlist.name

      if (this.watchlist) {
        this.watchlistItems = this.watchlist.watched.items

        // only use watchlistItems
        delete this.watchlist.watched
        this.loadingWatchlist = false

        // only create subscriptions for active watchlist
        if (!this.watchlist.inactive) {
          // subscribe to wlentry changes -> check exceeded object
          this.subscribeToChanges(
            this.watchlist,
            watchlistDetailsWLEntrySubscription,
            (wl) => {
              return {owner: this.userId, id: wl.id}
            },
            this.handleWLEntrySubscription
          )

          // subscribe to individual instrument changes
          this.subscribeToChanges(
            this.watchlistItems,
            watchlistDetailsInstrumentSubscription,
            (wlItem) => {
              return {id: wlItem.instrumentId}
            },
            this.handleInstrumentSubscription
          )
        }
      }
    },
    async fetchWLName() {
      const wl = await finUserAPI.getWatchlistName(this.id)
      if (wl) {
        this.watchlistTitle = wl.name
      }
    },
    deleteWatchlistEntry(entry) {
      finWatchlistAPI.deleteWatchlistEntry(entry.watchlistId, entry.instrumentId)

      try {
        this.watchlistItems = this.watchlistItems.filter(witem => witem.instrumentId !== entry.instrumentId)
        this.$toasted.global.finToast({
          message: 'Der Eintrag wurde aus der Watchliste entfernt.',
          type: 'success'
        })
      } catch (e) {
        console.error('deleteWatchlistEntry failed', e)
        this.$toasted.global.finToast({
          message: 'Beim Entfernen aus der Watchliste trat ein Fehler auf. Lade die Seite neu und versuche es bitte erneut..',
          type: 'error'
        })
      }
    },
    showRenameWatchlistDialog() {
      this.$refs.rename.show()
    },
    async updateWatchlistName(item, name) {
      const wl = {
        'id': item.id,
        'name': name
      }
      await finWatchlistAPI.renameWatchlist(wl)
      this.$set(item, 'name', name)
    },
    showDeleteWatchlistDialog() {
      let watchlistName = this.watchlist.name
      this.$confirm({
        title: 'Watchlist löschen',
        text: 'Möchtest du das Watchlist ' + watchlistName + ' wirklich löschen?',
        dialogWidth: 448,
        confirmBtn: 'Löschen'
      })
        .then(() => {
          this.deleteWatchlist(this.watchlist)
            .then(() => {
              this.$toasted.global.finToast({
                message: 'Das Watchlist %name% wurde erfolgreich gelöscht.',
                placeholders: [{name: 'name', content: watchlistName}],
                type: 'success'
              })
              this.$router.push({name: 'myWatchlists', params: {}})
            })
            .catch((e) => {
              console.error('deleteWatchlist failed', e)
              this.$toasted.global.finToast({
                message: 'Beim Löschen ist ein Fehler aufgetreten.',
                type: 'error'
              })
            })
        })
    },
    async deleteWatchlist(item) {
      await finWatchlistAPI.deleteWatchlist(item)
    },
    addItemsToPortfolio(item) {
      this.$router.push({
        name: 'addWatchlistsInstrumentsToPortfolio',
        params: {
          assetClassId: item.instrument.assetClass,
          instrumentId: item.instrument.id.toLowerCase()
        }
      })
    },
    gotoAlertsConfiguration(item) {
      this.$router.push({
        name: 'WatchlistNotification',
        params: {id: item.watchlistId, itemId: item.instrument.id}
      })
    },
    submitAssetClass(selectedItem) {
      this.selectedAssetClass = selectedItem ? AssetClasses.items.find(item => item.id === selectedItem) : this.selectedAssetClass
      this.addToWatchlistDialog = false
      this.$router.push({
        name: 'addInstrumentsToWatchlist',
        params: {
          assetClassId: this.selectedAssetClass.id,
        }
      })
    },
    async changeAlertActive(item) {
      try {
        item = await finUserAPI.updateWatchlistEntry(
          item.watchlistId,
          item.instrumentId,
          item.alertActive)
        this.$toasted.global.finToast({
          message: 'Der Benachrichtigungsstatus für %name% wwurde erfolgreich geändert.',
          placeholders: [{name: 'name', content: item.instrument.name}],
          type: 'success'
        })
      } catch (error) {
        console.error("error changing watchlist entry alertActive status", error)
        this.$toasted.global.finToast({
          message: `Ein Fehler ist beim Aktualisieren des Banachrichtigungsstatus für %name% aufgetreten.`,
          placeholders: [{name: "name", content: item.instrument.name}],
          type: 'error'
        })
        // revert to old value
        this.$nextTick(() => {
          item.alertActive = !item.alertActive
        })
      }
    },
    deleteLimitsConfirm(wlItem) {
      this.$confirm({
        title: 'Limits löschen',
        text: 'Möchtest du die Limits für ' + wlItem.instrument.name + ' wirklich löschen?',
        confirmBtn: 'Löschen',
        textWithLexikonLink: false,
        cancelBtn: 'Abbrechen'
      }).then(() => {
        this.deleteLimits(wlItem)
      })
    },
    async deleteLimits(wlItem) {
      try {
        wlItem = await finUserAPI.updateWatchlistEntry(
          wlItem.watchlistId,
          wlItem.instrumentId,
          false,
          {
            lowerLimit: null,
            upperLimit: null
          })
        this.$toasted.global.finToast({
          message: 'Die Limits für %name% wurden erfolgreich gelöscht.',
          placeholders: [{name: 'name', content: wlItem.instrument.name}],
          type: 'success'
        })
      } catch (error) {
        console.error("error deleting watchlist limits", error)
        this.$toasted.global.finToast({
          message: `Ein Fehler ist beim Löschen der Limits für %name% aufgetreten.`,
          placeholders: [{name: "name", content: wlItem.instrument.name}],
          type: 'error'
        })
      }
    },
    async addItemsToWatchlist(newWLItems) {
      const promises = []
      for (let i = 0; i < newWLItems.length; i++) {
        promises.push(finWatchlistAPI.createWatchlistEntry(this.watchlist.id, newWLItems[i].id))
      }
      const single = promises.length === 1 ? 'wurde' : 'wurden'
      const msg = promises.length === 1 ? 'neues Wertpapier ' : 'neue Wertpapiere '
      const errorMsg = promises.length === 1 ? 'neuen Wertpapier ' : 'neuen Wertpapieren '

      try {
        await Promise.all(promises)

        await this.fetchData()
        this.$toasted.global.finToast({
          message: `Es ${single} ${promises.length} ${msg} zur Watchlist hinzugefügt.`,
          type: 'success'
        })
      } catch (error) {
        console.log("error adding watchlist items", error)
        this.$toasted.global.finToast({
          message: `Ein Fehler ist beim Hinzufügen von ${promises.length} ${errorMsg} zur Watchlist aufgetreten.`,
          type: 'error'
        })
      }
    }
  },
  mounted() {
    // if the user is at the addInstruments page (wl id + newPage is set) => load the wl name), see FINDEV-2039
    if (this.id && this.$route.meta.newPage) {
      this.fetchWLName()
    }
    EventBus.$on('addItemsToWatchlist', this.addItemsToWatchlist)
  },
  beforeDestroy() {
    EventBus.$off('addItemsToWatchlist', this.addItemsToWatchlist)
  }
}
</script>

<style scoped lang="scss">
.content {
  background-color: #ffffff;

  ::v-deep tbody tr {
    height: 80px !important;
  }

  .item-name {
    font-weight: bold;
    font-size: 16px;
  }

  .item-name-link {
    text-decoration: none;
  }

  .item-close {
    font-weight: bold;
  }
}

.v-tooltip__content.menuable__content__active {
  opacity: 100% !important;
  border: 1px solid lightgray !important;
  max-width: 280px;
  text-align: center;
  border-radius: 0;
  box-shadow: 2px 2px 2px lightgray;
}

.v-btn > .v-btn__content .v-icon {
  color: rgba(0, 0, 0, 0.54);
}


.menu {
  background-color: #fff;
}

.realtime {
  position: relative !important;
  top: -7px !important;
  left: 5px !important;
}
</style>
